import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, Pressable, RefreshControl, TextInput } from 'react-native';

import { StyleSheet } from 'react-native-unistyles';
import { useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { useRouter, useNavigation } from 'expo-router';
import SheetModal from '@/components/General/SheetModal';
import SearchBox from '@/components/General/inputs/SearchBox';
import { ScrollView } from 'react-native-gesture-handler';
import SolidButton from '@/components/General/SolidButton';
import usePlatform from '@/utils/hooks/usePlatform';
import { DateTime } from 'luxon';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useMutation } from '@tanstack/react-query';
import { saveBatch, deleteBatch, deleteTransaction, getAllBatchTransactions } from '@/data/contributions';
import { ActivityIndicator } from 'react-native-paper';
import { queryClient } from '@/data/client';
import PageTitle from '@/components/General/PageTitle';
import DataExporter from '@/utils/DataExporter';
import { showAlert } from '@/utils/showAlert';
import Unicon from '@/components/General/Unicon';
import Toast from 'react-native-toast-message';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Checkbox from '@/components/General/inputs/Checkbox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';

const ExportOptionsModal = ({ isOpen, onClose, onConfirm }) => {
    const [exportFundAmountsAsRows, setExportFundAmountsAsRows] = useState(false);

    return (
        <SheetModal
            title="Export Options"
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            cancelText="Cancel"
            confirmText="Export"
            onConfirm={() => {
                onConfirm({ exportFundAmountsAsRows });
                onClose();
            }}
        >
            <View style={styles.sheetContent}>
                <Pressable
                    style={styles.exportOptionRow}
                    onPress={() => setExportFundAmountsAsRows(!exportFundAmountsAsRows)}
                >
                    <Checkbox
                        checked={exportFundAmountsAsRows}
                        onChange={() => setExportFundAmountsAsRows(!exportFundAmountsAsRows)}
                        size={20}
                    />
                    <View style={styles.exportOptionText}>
                        <Text style={styles.exportOptionLabel}>Export Fund Amounts as Rows</Text>
                        <Text style={styles.exportOptionHelpText}>
                            Splits each transaction into multiple rows — one per fund — so fund names and amounts appear on their own row instead of being grouped in a single column.
                        </Text>
                    </View>
                </Pressable>
            </View>
        </SheetModal>
    );
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

const TransactionsTab = React.memo(({ 
    batch, isLoadingBatch, isLoadingTransactions, isFetchingMoreTransactions,
    hasNextTransactionPage, fetchNextTransactionPage, allTransactions,
    sortConfig, setSortConfig, setTransactionSearchText, columns, tableActions,
    deleteTransactionMutation, router, theme, styles
}) => (
    <View style={styles.tabContent}>
        <Table
            isLoading={isLoadingBatch || isLoadingTransactions}
            title="Transactions"
            batchSelection
            batchActions={[{
                name: "Delete",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: (mode, ids, selectedCount) => showAlert({
                    title: "Delete Transactions",
                    subtitle: "Are you sure you want to delete " + selectedCount + " selected transactions? You will not be able to recover them.",
                    isDestructive: true,
                    onConfirm: () => deleteTransactionMutation.mutate({ ids, mode, batchID: batch?.id }),
                    confirmText: "Batch Delete"
                })
            }]}
            ListEmptyComponent={<EmptyListPlaceholder message="No transactions found" />}
            description="All transactions for the selected batch"
            keyExtractor={(item) => item.id}
            columns={columns}
            data={allTransactions}
            sortConfig={sortConfig}
            onSortChange={setSortConfig}
            onSearchChange={setTransactionSearchText}
            onRowClick={(item) => !batch?.isReachGiving && router.push({ pathname: "/contributions/transactions/create", params: { batchID: batch?.id, id: item.id } })}
            actions={tableActions}
            onEndReachedThreshold={0.5}
            onEndReached={() => {
                if (!isLoadingTransactions && !isFetchingMoreTransactions && hasNextTransactionPage) {
                    fetchNextTransactionPage();
                }
            }}
            ListFooterComponent={isFetchingMoreTransactions ? (
                <View style={{ paddingVertical: 20, alignItems: 'center' }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                </View>
            ) : null}
        />
    </View>
));

const ContributionsScreen = () => {
    const { isLandscape, isNative } = usePlatform();
    

    const [selectedBatch, setSelectedBatch] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [transactionSearchText, setTransactionSearchText] = useState('');
    const [debouncedTransactionSearch, setDebouncedTransactionSearch] = useState('');
    const [manageFundsSheetOpen, setManageFundsSheetOpen] = useState(false);
    const [manageMethodsSheetOpen, setManageMethodsSheetOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const [openBatchesModalOpen, setOpenBatchesModalOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [exportOptionsModalOpen, setExportOptionsModalOpen] = useState(false);
    const [editingBatchName, setEditingBatchName] = useState(false);
    const [batchNameInput, setBatchNameInput] = useState('');

    const { theme } = useUnistyles();
    const router = useRouter();
    const navigation = useNavigation();

    const { data: allBatches } = Query.contributionBatches.all();
    const { data: batch, isFetching: isLoadingBatch } = selectedBatch
        ? Query.contributionBatches.byId({ id: selectedBatch.id })
        : Query.contributionBatches.latest();
    const { data: funds } = Query.contributionFunds.all({ options: { enabled: manageFundsSheetOpen } });
    const { data: methods } = Query.contributionMethods.all({ options: { enabled: manageMethodsSheetOpen } });
    const { data: loggedInUser } = Query.people.loggedInUser();

    const batchID = batch?.id;
    const {
        data: transactionsData,
        isLoading: isLoadingTransactions,
        isFetchingNextPage: isFetchingMoreTransactions,
        hasNextPage: hasNextTransactionPage,
        fetchNextPage: fetchNextTransactionPage,
    } = Query.contributionBatches.transactions({
        batchID,
        searchText: debouncedTransactionSearch,
        sortConfig,
        options: { enabled: !!batchID },
    });

    const { data: batchStats } = Query.contributionBatches.stats({
        batchID,
        options: { enabled: !!batchID },
    });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTransactionSearch(transactionSearchText), 400);
        return () => clearTimeout(timer);
    }, [transactionSearchText]);

    useEffect(() => {
        setEditingBatchName(false);
    }, [batchID]);

    const openBatches = useMemo(() => {
        return (allBatches || [])
            .filter(b => b.isOpen)
            .sort((a, b) => (b.batchNumber || 0) - (a.batchNumber || 0));
    }, [allBatches]);

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title="Giving" />,
            headerRight: () => {
                const options = [];
                if (loggedInUser?.can?.("Create Contributions")) {
                    options.push({ name: "Open a New Batch", onSelect: () => router.push("/contributions/batches/create") });
                    options.push({ name: "Manage Funds", onSelect: () => setManageFundsSheetOpen(true) });
                    options.push({ name: "Manage Methods", onSelect: () => setManageMethodsSheetOpen(true) });
                }
                return options.length == 0 ? <></> : <DropdownMenu testID="GivingPageMenu" options={options} />;
            },
        });
    }, [batch, navigation, loggedInUser]);

    const exportTransactionsToCSV = async (options = {}) => {
        const allTransactions = await getAllBatchTransactions(batch?.id);
        let transactions = [];
        if (options.exportFundAmountsAsRows) {
            for (const t of allTransactions) {
                const base = {
                    "transactionID": t.id,
                    "batchID": batch?.id,
                    "batchName": batch?.name,
                    "payeeID": t.Person?.id ?? '',
                    "payeeName": t.Person ? `${t.Person.firstName ?? ''} ${t.Person.lastName ?? ''}`.trim() : 'Unknown',
                    "method": t.Method?.name ?? 'Unknown',
                    "methodDetailName": t.Method?.detailName ?? '',
                    "methodDetailValue": t.methodDetailValue ?? '',
                    "transactionTotal": t.amount,
                    "date": t.transactionDate ? DateTime.fromISO(t.transactionDate).toLocaleString(DateTime.DATETIME_MED) : '',
                    "note": t.note ?? ''
                };
                const funds = t.Funds?.filter(f => f.Fund);
                if (funds?.length > 0) {
                    for (const f of funds) {
                        transactions.push({
                            ...base,
                            "fundName": f.Fund.name,
                            "fundAmount": f.amount
                        });
                    }
                } else {
                    transactions.push({
                        ...base,
                        "fundName": '',
                        "fundAmount": ''
                    });
                }
            }
        } else {
            transactions = allTransactions.map(t => ({
                "transactionID": t.id,
                "batchID": batch?.id,
                "batchName": batch?.name,
                "payeeID": t.Person?.id ?? '',
                "payeeName": t.Person ? `${t.Person.firstName ?? ''} ${t.Person.lastName ?? ''}`.trim() : 'Unknown',
                "method": t.Method?.name ?? 'Unknown',
                "methodDetailName": t.Method?.detailName ?? '',
                "methodDetailValue": t.methodDetailValue ?? '',
                "funds": t.Funds?.map(f => f.Fund ? `${f.Fund.name},${f.amount}` : '').filter(Boolean).join("; "),
                "transactionTotal": t.amount,
                "date": t.transactionDate ? DateTime.fromISO(t.transactionDate).toLocaleString(DateTime.DATETIME_MED) : '',
                "note": t.note ?? ''
            }));
        }
        const sanitizedBatchName = (batch?.name || 'batch').replace(/[\\/:*?"<>|]/g, '');
        const timestamp = DateTime.now().toFormat('MMddyyyyHHmmss');
        DataExporter.exportToCSV(transactions, `reach_giving_${sanitizedBatchName}_${timestamp}`);
    };

    const saveBatchMutation = useMutation({
        mutationFn: saveBatch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: batch?.id ? QueryKeys.contributionBatches.byId(batch?.id || null) : QueryKeys.contributionBatches.latest() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.all() });
            if (batch?.id) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.stats(batch.id) });
            }
        },
    });

    const deleteBatchMutation = useMutation({
        mutationFn: deleteBatch,
        onSuccess: () => {
            Toast.show({ type: "success", text2: "Batch deleted successfully" });
            queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.latest() });
            setSelectedBatch(null);
        },
        onError: (error) => {
            Toast.show({ type: "error", text2: error?.message || "Failed to delete batch" });
        },
    });

    const deleteTransactionMutation = useMutation({
        mutationFn: deleteTransaction,
        onSuccess: () => {
            Toast.show({ type: "success", text2: "Transaction deleted successfully" });
            queryClient.invalidateQueries({ queryKey: ['contributions', 'batches'] });
        },
    });

    const allTransactions = useMemo(() => {
        if (!transactionsData?.pages) return [];
        return transactionsData.pages.flatMap(page => page.data);
    }, [transactionsData]);

    const columns = useMemo(() => [
        {
            value: 'Person',
            accessor: 'person',
            sortable: true,
            renderer: (item) => {
                if (!item.Person) {
                    return (
                        <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                            <Avatar profile={null} size={35} disableBorder />
                            <View><Text style={{ fontFamily: theme.fonts.bold, color: theme.colors.text }}>Unknown</Text></View>
                        </View>
                    );
                }
                return (
                    <Pressable onPress={() => router.push({ pathname: `/people/${item.Person.id}` })} style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                        <Avatar profile={item.Person} size={35} disableBorder />
                        <View>
                            <Text style={{ fontFamily: theme.fonts.bold, color: theme.colors.text }}>{item.Person.firstName} {item.Person.lastName}</Text>
                            <Text style={{ color: theme.colors.textSecondary, fontSize: 12 }}>{item.Person.email}</Text>
                        </View>
                    </Pressable>
                );
            }
        },
        {
            value: 'Method',
            accessor: 'method',
            sortable: true,
            renderer: (item) => (
                <Text style={{ fontFamily: theme.fonts.body, color: theme.colors.text }}>{item.Method?.name ?? '-'}</Text>
            )
        },
        {
            value: 'Details',
            accessor: 'details',
            sortable: true,
            renderer: (item) => (
                <View>
                    <Text style={{ fontFamily: theme.fonts.body, color: theme.colors.text }}>{item.Method?.detailName ?? '-'}</Text>
                    <Text style={{ fontFamily: theme.fonts.body, color: theme.colors.textSecondary, fontSize: 12 }}>{item.methodDetailValue ?? "-"}</Text>
                </View>
            )
        },
        {
            value: 'Funds',
            accessor: 'funds',
            sortable: true,
            renderer: (item) => {
                if (!item.Funds?.length) {
                    return <Text style={{ color: theme.colors.textSecondary, fontSize: 12 }}>-</Text>;
                }
                return (
                    <View>
                        {item.Funds.map(fundTransaction => {
                            if (!fundTransaction.Fund) return null;
                            return (
                                <View key={fundTransaction.Fund.id} style={styles.label}>
                                    <Text style={{ color: theme.colors.primary }}>{fundTransaction.Fund.name}</Text>
                                    <Text style={{ color: theme.colors.textSecondary, fontSize: 12 }}>${Number(fundTransaction.amount).toFixed(2)}</Text>
                                </View>
                            );
                        })}
                    </View>
                );
            }
        },
        {
            value: 'Notes/Memo',
            accessor: 'note',
            sortable: true,
            renderer: (item) => (
                <View>
                    <Text style={{ fontFamily: theme.fonts.body, color: theme.colors.textSecondary, fontSize: 12 }}>{item.note}</Text>
                </View>
            )
        },
        {
            value: 'Total Amount',
            accessor: 'amount',
            sortable: true,
            renderer: (item) => (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                    <Text style={{ fontFamily: theme.fonts.body, color: theme.colors.success }}>{formatCurrency(item.amount)}</Text>
                </View>
            )
        },
        {
            value: 'Date',
            accessor: 'date',
            sortable: true,
            renderer: (item) => (
                <View style={{ gap: 5 }}>
                    <Text style={{ fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary }}>{DateTime.fromISO(item.transactionDate).toLocaleString(DateTime.DATE_MED)}</Text>
                    <Text style={{ fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary }}>{DateTime.fromISO(item.transactionDate).toLocaleString(DateTime.TIME_SIMPLE)}</Text>
                </View>
            )
        },
    ], [theme.colors, theme.fonts]);

    const totalContributions = batchStats?.totalAmount || 0;
    const totalContributionsByMethod = batchStats?.byMethod || {};
    const totalContributionsByFund = batchStats?.byFund || {};
    const totalUniqueGivers = batchStats?.uniqueGivers || 0;

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: batch?.id ? QueryKeys.contributionBatches.byId(batch.id) : QueryKeys.contributionBatches.latest() });
        if (batch?.id) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.transactions(batch.id, debouncedTransactionSearch, sortConfig) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.contributionBatches.stats(batch.id) });
        }
    };

    const tableActions = useMemo(() => {
        const actions = [];
        if (batch?.isReachGiving) return actions;
        loggedInUser?.can?.("Create Contributions") && actions.push({
            name: "Edit",
            icon: dropdownIcons.edit,
            onSelect: (item) => router.push({ pathname: "/contributions/transactions/create", params: { batchID: batch?.id, id: item.id } })
        });
        loggedInUser?.can?.("Delete Contributions") && actions.push({
            name: "Delete",
            icon: dropdownIcons.delete,
            destructive: true,
            onSelect: (item) => showAlert({
                title: "Delete Transaction",
                subtitle: "Are you sure you want to delete this transaction? You will not be able to recover it.",
                isDestructive: true,
                onConfirm: () => deleteTransactionMutation.mutate({ ids: [item.id], batchID: batch?.id }),
                confirmText: "Delete"
            })
        });
        return actions;
    }, [loggedInUser, batch?.id, batch?.isReachGiving]);

    const batchInfoPanel = useMemo(() => (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isLoadingBatch} onRefresh={refresh}/>}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <View style={styles.headerCard}>
                    {/* Batch Selector + Status Row */}
                    <View style={styles.batchSelectorWrapper}>
                        <SheetSelector
                            titleOnly
                            title="Batch"
                            placeholder="Select a Batch"
                            value={batch?.id ? batch : null}
                            type="contributionBatches"
                            onChange={(new_value) => setSelectedBatch(new_value)}
                        />
                    </View>

                    <View style={styles.metaRow}>
                        {!isLoadingBatch && batch?.id && (
                            <View style={[styles.statusBadge, { backgroundColor: batch?.isOpen ? theme.colors.success + '20' : theme.colors.error + '20' }]}>
                                <View style={[styles.statusDot, { backgroundColor: batch?.isOpen ? theme.colors.success : theme.colors.error }]} />
                                <Text style={[styles.statusText, { color: batch?.isOpen ? theme.colors.success : theme.colors.error }]}>
                                    {batch?.isOpen ? "Open" : "Closed"}
                                </Text>
                            </View>
                        )}
                        {batch?.isReachGiving && (
                            <View style={[styles.statusBadge, { backgroundColor: theme.colors.giving + '20' }]}>
                                <Unicon name="heart-outline" size={12} colorVariant="giving" />
                                <Text style={[styles.statusText, { color: theme.colors.giving }]}>
                                    Reach Giving
                                </Text>
                            </View>
                        )}
                        {openBatches.length > 0 && (
                            <Pressable style={[styles.statusBadge, { backgroundColor: theme.colors.textSecondary + '15' }]} onPress={() => setOpenBatchesModalOpen(true)}>
                                <View style={[styles.statusDot, { backgroundColor: theme.colors.textSecondary }]} />
                                <Text style={[styles.statusText, { color: theme.colors.textSecondary }]}>
                                    {openBatches.length} open
                                </Text>
                            </Pressable>
                        )}
                    </View>

                    {/* Quick Stats */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Total Amount</Text>
                            <Text style={styles.statValue}>{totalContributions ? formatCurrency(totalContributions) : "$0.00"}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Transactions</Text>
                            <Text style={styles.statValue}>{batchStats ? (batchStats.transactionCount ?? 0) : '-'}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Givers</Text>
                            <Text style={styles.statValue}>{batchStats ? totalUniqueGivers : '-'}</Text>
                        </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {loggedInUser?.can?.("Create Contributions") && batch?.id && !batch?.isReachGiving && (
                            <View style={styles.actionButtonWrapper}>
                                <SolidButton variant="muted" iconName="add" iconBefore text="Add" tooltipText="Add a new transaction to this batch" width="100%" onPress={() => router.push({ pathname: "/contributions/transactions/create", params: { batchID: batch?.id } })} />
                            </View>
                        )}
                        {loggedInUser?.can?.("Create Contributions") && batch?.id && !batch?.isReachGiving && (
                            <View style={styles.actionButtonWrapper}>
                                <SolidButton variant="muted" iconName="cloud-upload-outline" iconBefore text="Import" tooltipText="Import bulk transactions from a CSV file into this batch" width="100%" onPress={() => router.push({ pathname: "/import/transactions", params: { batchID: batch?.id } })} />
                            </View>
                        )}
                        <View style={styles.actionButtonWrapper}>
                            <SolidButton variant="muted" iconName="download-outline" iconBefore text="Export" tooltipText="Export all transactions in the batch into a CSV file" width="100%" onPress={() => setExportOptionsModalOpen(true)} />
                        </View>
                        {!isLoadingBatch && batch?.id && !batch?.isReachGiving && loggedInUser?.can?.("Create Contributions") && (
                            <View style={styles.actionButtonWrapper}>
                                <SolidButton variant="muted" iconName={batch?.isOpen ? "lock-closed-outline" : "lock-open-outline"} iconBefore text={batch?.isOpen ? "Close" : "Reopen"} tooltipText={batch?.isOpen ? "Close this batch" : "Reopen this batch"} width="100%" onPress={() => saveBatchMutation.mutate({ id: batch?.id, isOpen: !batch?.isOpen })} />
                            </View>
                        )}
                        {!isLoadingBatch && batch?.id && batchStats?.transactionCount === 0 && loggedInUser?.can?.("Delete Contributions") && !batch?.isReachGiving && (
                            <View style={styles.actionButtonWrapper}>
                                <SolidButton variant="muted" type="error" iconName="trash-outline" iconBefore text="Delete" tooltipText="Delete this empty batch" width="100%" onPress={() => showAlert({
                                    title: "Delete Batch",
                                    subtitle: "Are you sure you want to delete this batch? It has no transactions.",
                                    isDestructive: true,
                                    onConfirm: () => deleteBatchMutation.mutate(batch?.id),
                                    confirmText: "Delete"
                                })} />
                            </View>
                        )}
                    </View>
                </View>

                {/* Batch Details */}
                {batch?.id && (
                    <View style={styles.infoSection}>
                        <Text style={styles.sectionLabel}>Batch Details</Text>
                        <View style={styles.infoRow}>
                            <View style={styles.infoRowLeft}>
                                <Unicon name="cash-outline" size={18} colorVariant="textSecondary" />
                                <Text style={styles.infoRowLabel}>Batch Name</Text>
                            </View>
                            <View style={styles.infoRowRight}>
                                {editingBatchName ? (
                                    <View style={styles.editNameRow}>
                                        <TextInput
                                            style={styles.editNameInput}
                                            value={batchNameInput}
                                            onChangeText={setBatchNameInput}
                                            autoFocus
                                            selectTextOnFocus
                                            testID="batch-name-input"
                                        />
                                        <Pressable
                                            onPress={() => {
                                                if (batchNameInput.trim() && batchNameInput.trim() !== batch?.name) {
                                                    saveBatchMutation.mutate({ id: batch?.id, name: batchNameInput.trim(), isOpen: batch?.isOpen, originalValues: { name: batch?.name, isOpen: batch?.isOpen } });
                                                }
                                                setEditingBatchName(false);
                                            }}
                                            style={styles.editNameConfirm}
                                        >
                                            <Unicon name="checkmark" size={18} colorVariant="success" />
                                        </Pressable>
                                        <Pressable
                                            onPress={() => setEditingBatchName(false)}
                                            style={styles.editNameCancel}
                                        >
                                            <Unicon name="close" size={18} colorVariant="textSecondary" />
                                        </Pressable>
                                    </View>
                                ) : (
                                    <Pressable
                                        onPress={() => {
                                            if (batch?.isReachGiving) return;
                                            setBatchNameInput(batch?.name || '');
                                            setEditingBatchName(true);
                                        }}
                                        style={styles.editNameTrigger}
                                    >
                                        <Text style={styles.infoRowValue}>{batch?.name}</Text>
                                        {loggedInUser?.can?.("Create Contributions") && !batch?.isReachGiving && (
                                            <Unicon name="create-outline" size={14} colorVariant="textSecondary" />
                                        )}
                                    </Pressable>
                                )}
                            </View>
                        </View>
                        <View style={styles.infoRow}>
                            <View style={styles.infoRowLeft}>
                                <Unicon name="pricetag-outline" size={18} colorVariant="textSecondary" />
                                <Text style={styles.infoRowLabel}>Batch #</Text>
                            </View>
                            <View style={styles.infoRowRight}>
                                <Text style={styles.infoRowValue}>{batch?.batchNumber}</Text>
                            </View>
                        </View>
                        <View style={styles.infoRow}>
                            <View style={styles.infoRowLeft}>
                                <Unicon name="calendar" size={18} colorVariant="textSecondary" />
                                <Text style={styles.infoRowLabel}>Date</Text>
                            </View>
                            <View style={styles.infoRowRight}>
                                <Text style={styles.infoRowValue}>{batch?.openedAt ? DateTime.fromISO(batch.openedAt).toLocaleString(DateTime.DATE_MED) : '-'}</Text>
                            </View>
                        </View>
                    </View>
                )}

                {/* Breakdown Section */}
                {totalContributions > 0 && (
                    <>
                        {totalContributionsByMethod && Object.keys(totalContributionsByMethod).length > 0 && (
                            <View style={styles.breakdownCard}>
                                <View style={styles.breakdownCardHeader}>
                                    <Text style={styles.sectionLabel}>By Method</Text>
                                    <Text style={styles.breakdownTotal}>{formatCurrency(Object.values(totalContributionsByMethod).reduce((a, b) => a + b, 0))}</Text>
                                </View>
                                {Object.entries(totalContributionsByMethod).map(([method, amount]) => (
                                    <View key={method} style={styles.breakdownRowItem}>
                                        <View style={styles.breakdownRowLeft}>
                                            <View style={[styles.breakdownDot, { backgroundColor: theme.colors.primary }]} />
                                            <Text style={styles.breakdownRowLabel}>{method}</Text>
                                        </View>
                                        <Text style={styles.breakdownRowAmount}>{formatCurrency(amount)}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                        {totalContributionsByFund && Object.keys(totalContributionsByFund).length > 0 && (
                            <View style={styles.breakdownCard}>
                                <View style={styles.breakdownCardHeader}>
                                    <Text style={styles.sectionLabel}>By Fund</Text>
                                    <Text style={styles.breakdownTotal}>{formatCurrency(Object.values(totalContributionsByFund).reduce((a, b) => a + b, 0))}</Text>
                                </View>
                                {Object.entries(totalContributionsByFund).map(([fund, amount]) => (
                                    <View key={fund} style={styles.breakdownRowItem}>
                                        <View style={styles.breakdownRowLeft}>
                                            <View style={[styles.breakdownDot, { backgroundColor: theme.colors.success }]} />
                                            <Text style={styles.breakdownRowLabel}>{fund}</Text>
                                        </View>
                                        <Text style={styles.breakdownRowAmount}>{formatCurrency(amount)}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </>
                )}
            </ScrollView>
        </View>
    ), [batch, isLoadingBatch, batchStats, totalContributions, totalUniqueGivers, totalContributionsByMethod, totalContributionsByFund, openBatches.length, loggedInUser, theme.colors, theme.fonts, editingBatchName, batchNameInput, saveBatchMutation, deleteBatchMutation]);


    const tabRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        { key: 'transactions', title: 'Transactions' },
    ], []);

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'info':
                return batchInfoPanel;
            case 'transactions':
                return (
                    <TransactionsTab
                        batch={batch}
                        isLoadingBatch={isLoadingBatch}
                        isLoadingTransactions={isLoadingTransactions}
                        isFetchingMoreTransactions={isFetchingMoreTransactions}
                        hasNextTransactionPage={hasNextTransactionPage}
                        fetchNextTransactionPage={fetchNextTransactionPage}
                        allTransactions={allTransactions}
                        sortConfig={sortConfig}
                        setSortConfig={setSortConfig}
                        setTransactionSearchText={setTransactionSearchText}
                        columns={columns}
                        tableActions={tableActions}
                        deleteTransactionMutation={deleteTransactionMutation}
                        router={router}
                        theme={theme}
                        styles={styles}
                    />
                );
            default:
                return null;
        }
    }, [batch, isLoadingBatch, isLoadingTransactions, isFetchingMoreTransactions, hasNextTransactionPage, fetchNextTransactionPage, allTransactions, sortConfig, columns, tableActions, deleteTransactionMutation, batchInfoPanel, theme, styles]);

    const modals = (
        <>
            <SheetModal
                title="Open Batches"
                isOpen={openBatchesModalOpen}
                onCancel={() => setOpenBatchesModalOpen(false)}
                cancelText="Close"
                confirmText=""
            >
                <View style={styles.sheetContent}>
                    {openBatches.length === 0 ? (
                        <Text style={styles.sheetSubtitle}>No open batches</Text>
                    ) : (
                        <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                            <View style={styles.sheetListContainer}>
                                {openBatches.map((b) => (
                                    <Pressable
                                        key={b.id}
                                        style={styles.sheetListItem}
                                        onPress={() => {
                                            setSelectedBatch(b);
                                            setOpenBatchesModalOpen(false);
                                        }}
                                    >
                                        <View style={styles.sheetListItemLeft}>
                                            <View style={styles.sheetBatchIcon}>
                                                <Unicon name="cash-outline" size={18} colorVariant="onPrimary" />
                                            </View>
                                            <View style={styles.sheetListItemInfo}>
                                                <Text style={styles.sheetListItemTitle} numberOfLines={1}>{b.name}</Text>
                                                <Text style={styles.sheetListItemSubtitle}>#{b.batchNumber}</Text>
                                            </View>
                                        </View>
                                        <View style={[styles.statusBadge, { backgroundColor: theme.colors.success + '20' }]}>
                                            <View style={[styles.statusDot, { backgroundColor: theme.colors.success }]} />
                                            <Text style={[styles.statusText, { color: theme.colors.success }]}>Open</Text>
                                        </View>
                                    </Pressable>
                                ))}
                            </View>
                        </ScrollView>
                    )}
                </View>
            </SheetModal>

            <SheetModal
                title="Manage Funds"
                isOpen={manageFundsSheetOpen}
                onCancel={() => setManageFundsSheetOpen(false)}
                confirmText=""
            >
                <View style={styles.sheetContent}>
                    <View style={styles.sheetHeader}>
                        <View style={styles.sheetSearchRow}>
                            <View style={styles.sheetSearchContainer}>
                                <SearchBox searchText={searchText} onChangeText={(e) => setSearchText(e)} textChangeDelay={0} placeholder="Search funds..." />
                            </View>
                            {loggedInUser?.can?.("Create Contributions") && (
                                <SolidButton
                                    size="sm"
                                    iconName="add"
                                    iconBefore
                                    text="New"
                                    onPress={() => {
                                        setManageFundsSheetOpen(false);
                                        router.push({ pathname: "/contributions/funds/create" });
                                    }}
                                />
                            )}
                        </View>
                        <Text style={styles.sheetSubtitle}>
                            {funds?.length || 0} fund{funds?.length !== 1 ? 's' : ''}
                        </Text>
                    </View>
                    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                        <View style={styles.sheetListContainer}>
                            {funds?.filter((fund) => fund.name.toLowerCase().includes(searchText.toLowerCase())).map((fund) => (
                                <Pressable
                                    key={fund.id}
                                    style={styles.sheetListItem}
                                    onPress={() => {
                                        if (loggedInUser?.can?.("Create Contributions")) {
                                            setManageFundsSheetOpen(false);
                                            router.push({ pathname: "/contributions/funds/create", params: { id: fund.id } });
                                        }
                                    }}
                                >
                                    <View style={styles.sheetListItemLeft}>
                                        <View style={styles.sheetFundIcon}>
                                            <Unicon name="cash-outline" size={18} colorVariant="onPrimary" />
                                        </View>
                                        <View style={styles.sheetListItemInfo}>
                                            <Text style={styles.sheetListItemTitle} numberOfLines={1}>{fund.name}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.sheetListItemRight}>
                                        <DropdownMenu
                                            options={[
                                                ...(loggedInUser?.can?.("Create Contributions") ? [{ name: "Edit Fund", onSelect: () => { setManageFundsSheetOpen(false); router.push({ pathname: "/contributions/funds/create", params: { id: fund.id } }); } }] : [])
                                            ]}
                                        />
                                    </View>
                                </Pressable>
                            ))}
                        </View>
                    </ScrollView>
                </View>
            </SheetModal>

            <SheetModal
                title="Manage Methods"
                isOpen={manageMethodsSheetOpen}
                onCancel={() => setManageMethodsSheetOpen(false)}
                confirmText=""
            >
                <View style={styles.sheetContent}>
                    <View style={styles.sheetHeader}>
                        <View style={styles.sheetSearchRow}>
                            <View style={styles.sheetSearchContainer}>
                                <SearchBox searchText={searchText} onChangeText={(e) => setSearchText(e)} textChangeDelay={0} placeholder="Search methods..." />
                            </View>
                            {loggedInUser?.can?.("Create Contributions") && (
                                <SolidButton
                                    size="sm"
                                    iconName="add"
                                    iconBefore
                                    text="New"
                                    onPress={() => {
                                        setManageMethodsSheetOpen(false);
                                        router.push({ pathname: "/contributions/methods/create" });
                                    }}
                                />
                            )}
                        </View>
                        <Text style={styles.sheetSubtitle}>
                            {methods?.length || 0} method{methods?.length !== 1 ? 's' : ''}
                        </Text>
                    </View>
                    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                        <View style={styles.sheetListContainer}>
                            {methods?.filter((method) => method.name.toLowerCase().includes(searchText.toLowerCase())).map((method) => (
                                <Pressable
                                    key={method.id}
                                    style={styles.sheetListItem}
                                    onPress={() => {
                                        if (loggedInUser?.can?.("Create Contributions")) {
                                            setManageMethodsSheetOpen(false);
                                            router.push({ pathname: "/contributions/methods/create", params: { id: method.id } });
                                        }
                                    }}
                                >
                                    <View style={styles.sheetListItemLeft}>
                                        <View style={styles.sheetMethodIcon}>
                                            <Unicon name="card-outline" size={18} colorVariant="onPrimary" />
                                        </View>
                                        <View style={styles.sheetListItemInfo}>
                                            <Text style={styles.sheetListItemTitle} numberOfLines={1}>{method.name}</Text>
                                            {method.detailName && <Text style={styles.sheetListItemSubtitle} numberOfLines={1}>{method.detailName}</Text>}
                                        </View>
                                    </View>
                                    <View style={styles.sheetListItemRight}>
                                        {loggedInUser?.can?.("Create Contributions") && (
                                            <Pressable onPress={() => {
                                                setManageMethodsSheetOpen(false);
                                                router.push({ pathname: "/contributions/methods/create", params: { id: method.id } });
                                            }}>
                                                <Unicon name="create-outline" colorVariant="text" size={20} />
                                            </Pressable>
                                        )}
                                    </View>
                                </Pressable>
                            ))}
                        </View>
                    </ScrollView>
                </View>
            </SheetModal>

            <ExportOptionsModal
                isOpen={exportOptionsModalOpen}
                onClose={() => setExportOptionsModalOpen(false)}
                onConfirm={(options) => exportTransactionsToCSV(options)}
            />
        </>
    );

    if (!loggedInUser?.can?.("View Contributions")) {
        return (
            <View style={{ flex: 1 }}>
                <View style={{ flexGrow: 1, flex: 1 }}>
                    <View style={{ padding: 20, textAlign: "center" }}>
                        <View style={{ alignItems: "center", width: "100%", gap: 10 }}>
                            <Text style={styles.detailText}>You do not have permission to view contributions.</Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    }

    if (isLandscape) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    {batchInfoPanel}
                    <View style={styles.rightPanel}>
                        {renderScene({ route: { key: 'transactions' } })}
                    </View>
                </View>
                {modals}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                renderScene={renderScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
            {modals}
        </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420, paddingHorizontal: 16, marginVertical: rt.isLandscape ? 0 : 16 },
    leftPanelContent: { paddingBottom: 100, gap: 4 },
    rightPanel: { flex: 2 },
    tabContent: { flex: 1 },
    listPadding: { padding: 16, paddingBottom: 100 },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },

    // Header Card
    headerCard: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: theme.shadows.card },

    // Batch Selector
    batchSelectorWrapper: { marginBottom: 12 },

    // Status Badge
    statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, marginBottom: 12, alignSelf: 'flex-start' },
    statusDot: { width: 8, height: 8, borderRadius: 4 },
    statusText: { fontSize: 12, fontFamily: theme.fonts.bold },

    // Meta Row (status + open batches inline)
    metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },

    // Stats Row
    statsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 20, marginBottom: 20, gap: 8 },
    statItem: { flex: 1, alignItems: 'center', gap: 6 },
    statDivider: { width: 1, alignSelf: 'stretch', backgroundColor: theme.colors.border + '40', marginVertical: 4 },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
    statValue: { fontSize: 16, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
    actionButtonWrapper: { flex: 1, flexBasis: '30%', minWidth: 0 },

    // Info Section
    infoSection: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: theme.shadows.card },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    infoRowValueSub: { fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right', marginTop: 2, fontStyle: 'italic' },
    linkText: { color: theme.colors.primary },

    // Edit Batch Name
    editNameTrigger: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    editNameRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    editNameInput: { fontSize: 14, color: theme.colors.text, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, minWidth: 120, textAlign: 'right' },
    editNameConfirm: { padding: 4 },
    editNameCancel: { padding: 4 },

    // Breakdown Cards
    breakdownCard: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: theme.shadows.card, gap: 12 },
    breakdownCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
    breakdownTotal: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    breakdownRowItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10 },
    breakdownRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    breakdownDot: { width: 8, height: 8, borderRadius: 4 },
    breakdownRowLabel: { fontSize: 14, color: theme.colors.text, fontFamily: theme.fonts.body },
    breakdownRowAmount: { fontSize: 14, fontFamily: theme.fonts.body, color: theme.colors.textSecondary },

    // Detail Text
    detailText: { fontSize: 12, fontFamily: theme.fonts.body, color: theme.colors.textSecondary },

    // Label (for fund tags in table)
    label: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingBottom: 2, paddingHorizontal: 8, borderRadius: 5, backgroundColor: theme.colors.primaryContainer, margin: 2, alignSelf: "flex-start" },

    // Sheet Styles
    sheetContent: { paddingBottom: 20 },
    sheetHeader: { marginBottom: 16 },
    sheetSearchRow: { flexDirection: 'row', gap: 10, alignItems: 'center' },
    sheetSearchContainer: { flex: 1 },
    sheetSubtitle: { fontSize: 13, fontFamily: theme.fonts.body, color: theme.colors.textSecondary, marginTop: 12 },
    sheetListContainer: { gap: 8, paddingBottom: 220 },
    sheetListItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: theme.colors.surface, borderRadius: 10, padding: 12, borderWidth: 1, borderColor: theme.colors.border, opacity: 1, _web: { transition: 'background-color 0.2s ease', _hover: { backgroundColor: theme.colors.hover } } },
    sheetListItemLeft: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
    sheetBatchIcon: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.primary + '20', alignItems: 'center', justifyContent: 'center' },
    sheetFundIcon: { width: 32, height: 32, borderRadius: 18, backgroundColor: theme.colors.primary, alignItems: 'center', justifyContent: 'center' },
    sheetMethodIcon: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.primary + '15', alignItems: 'center', justifyContent: 'center' },
    sheetListItemInfo: { flex: 1 },
    sheetListItemTitle: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text },
    sheetListItemSubtitle: { fontSize: 13, fontFamily: theme.fonts.body, color: theme.colors.textSecondary, marginTop: 2 },
    sheetListItemRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },

    // Export Options
    exportOptionRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 12, backgroundColor: theme.colors.surface, borderRadius: 10, borderWidth: 1, borderColor: theme.colors.border },
    exportOptionText: { flex: 1, gap: 4 },
    exportOptionLabel: { fontSize: 14, fontFamily: theme.fonts.heading, color: theme.colors.text },
    exportOptionHelpText: { fontSize: 12, fontFamily: theme.fonts.body, color: theme.colors.textSecondary, lineHeight: 18 }
}));

export default ContributionsScreen;
